import React, { useEffect, useRef, useState } from 'react';
import { Button, Link, Stack, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary } from '../components/Accordian';
import UploadCsv from '../components/UploadCsv';
import DataSourceType from '../DataSourceType';
import { ReactComponent as ArrowDownIcon } from '../resources/images/arrow_down.svg';
import { ReactComponent as ConfigEditIcon } from '../resources/images/edit_config.svg';
import HexagonHovered from '../resources/images/hexagon-hovered.svg';
import HexagonSelected from '../resources/images/hexagon-selected.svg';
import Hexagon from '../resources/images/hexagon.svg';
import CSVHexagonLogo from '../resources/images/select_source_purple.svg';
import ServiceMaxLogo from '../resources/images/servicemax-logo.png';
import UploadCsvLogo2 from '../resources/images/upload-logo-2.svg';
import { WizardStore } from '../stores/WizardStore';
import SFConnector from '../tools/SFConnector';
import './Wizard.scss';

export type DataSourceProps = {
    name: DataSourceType;
    logo?: string;
    logo2?: string;
    label?: string;
};

const mockSources: DataSourceProps[] = [
    // {name: DataSourceType.sfdc, logo: SalesForceLogo},
    { name: DataSourceType.svmx, logo: ServiceMaxLogo, logo2: ServiceMaxLogo, label: 'CRM Connection' },
    { name: DataSourceType.csv, logo: CSVHexagonLogo, logo2: UploadCsvLogo2, label: 'Upload File' },
];

const CleanStack = styled(Stack)({
    margin: 'unset',
});

const DataSource = observer(({ wizardStore }: { wizardStore: WizardStore }) => {
    const { t } = useTranslation();
    const { configStore } = wizardStore;
    const selectedSource = mockSources.find((source) => source.name === configStore.source_type?.name);
    const { setSourceType, setSourceCreds } = configStore;
    const { template_config, setTemplateConfig } = configStore;
    const { setMasterFiles } = configStore;
    const { showProgress } = configStore;
    const { expandedTab } = wizardStore;
    const [hoveredSource, setHoveredSource] = useState<DataSourceType>();
    const [expanded, setExpanded] = useState<string | boolean>('select-source');
    const [nextButtonDisable, setnextButtonDisable] = useState<boolean>(!selectedSource);
    const [loggedIn, setLoggenIn] = useState<boolean>(false);
    const firstTimeEditLoaded = useRef<boolean>(false);

    const renderSource = (source: DataSourceProps, action?: () => void) => {
        const backgroundUrl =
            selectedSource && source.name === selectedSource.name
                ? HexagonSelected
                : source.name === hoveredSource
                  ? HexagonHovered
                  : Hexagon;

        return (
            <div
                data-testid={`data-source-${source.name}`}
                onClick={action}
                onMouseEnter={() => {
                    if (action) {
                        setHoveredSource(source.name);
                    }
                }}
                onMouseLeave={() => {
                    if (action) {
                        setHoveredSource(undefined);
                    }
                }}
                key={source.name}
                style={{
                    height: 135,
                    width: 135,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: action ? 'pointer' : 'default',
                }}
            >
                <img src={backgroundUrl} alt="source-background" style={{ position: 'absolute' }} />
                <img
                    style={{ width: '55%', height: '55%', objectFit: 'contain', zIndex: 1 }}
                    src={source.logo}
                    alt={`${source.name}logo`}
                />
                {!action && (
                    <div className="SoonTag">
                        <Typography fontSize={10} color="white" textTransform="capitalize" lineHeight={1.4}>
                            {' '}
                            soon{' '}
                        </Typography>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (
            expandedTab &&
            !firstTimeEditLoaded.current &&
            template_config &&
            selectedSource &&
            template_config.environments.length > 0
        ) {
            handleExpandedChange(expandedTab);
            setSourceType(mockSources.find((source) => source.name === selectedSource.name));
            firstTimeEditLoaded.current = true;
        }
    }, [expandedTab, setSourceType, selectedSource, template_config]);

    const handleStepChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded ? panel : false);
    };

    const handleExpandedChange = (exp: string) => {
        setExpanded(exp);
    };

    const setSource = (source) => {
        if (!template_config) {
            return;
        }
        template_config.config_type = null;
        if (template_config.environments) {
            template_config.environments[0].env_type = null;
            template_config.environments[0].env_locale = null;
            template_config.environments[0].resource_url = null;
            template_config.environments[0].login_url = null;
            template_config.environments[0].access_token = null;
        }
        setMasterFiles([]);
        setSourceType(source);
        template_config.schedule = { schedule_interval: 'Manual' };
        template_config.environments[0].env_type = 'Source';
        template_config.environments[0].env_locale = 'en_US';
        switch (source.name) {
            case DataSourceType.svmx:
                template_config.config_type = DataSourceType.svmx;
                setTemplateConfig({ ...template_config });
                break;
            case DataSourceType.csv:
                template_config.environments[0].resource_url = 'csv://';
                template_config.environments[0].login_url = 'csv://';
                template_config.config_type = DataSourceType.csv;

                setTemplateConfig({ ...template_config });
                setSourceCreds(undefined);
                setLoggenIn(false);
                break;
            default:
                break;
        }
    };

    const SFConnectorCallback = (credentials) => {
        template_config.environments[0].resource_url = credentials.instance_url;
        template_config.environments[0].login_url = credentials.login_url;
        template_config.environments[0].access_token = credentials.access_token;
        setSourceCreds({
            source_domain: credentials.login_url,
            source_user_info: {
                refresh_token: credentials.refresh_token,
                access_token: credentials.access_token,
                instance_url: credentials.instance_url,
            },
        });
        setLoggenIn(true);
        setTemplateConfig({ ...template_config });
    };

    return (
        <CleanStack spacing={2} direction="column" className="DataSource">
            <Stack direction="column">
                <Accordion
                    data-testid="step1DataSelect"
                    disabled={expandedTab !== undefined}
                    expanded={expanded === 'select-source'}
                    onChange={handleStepChange('select-source')}
                >
                    <AccordionSummary
                        aria-controls="content2"
                        expanded={expanded === 'select-source'}
                        id="id2"
                        title={t('wizard.data-source.select-source')}
                        thisStep={1}
                        totalSteps={3}
                        stepTitle={t('common.accordian-step-title')}
                        data-testid="step1DataSelectTitleContainer"
                        dataTestIdTitle="step1DataSelectTitle"
                        dataTestIdStep="step1DataSelectStep"
                    >
                        <Stack>
                            {selectedSource ? (
                                <Stack direction="row">
                                    <img
                                        style={{
                                            height: '12px',
                                            objectFit: 'contain',
                                            zIndex: 1,
                                            marginLeft: '40px',
                                            marginTop: '4px',
                                        }}
                                        src={selectedSource.logo2}
                                        className="BreadcrumbsLogo"
                                        alt="selected-source-logo"
                                    />
                                    <Typography marginLeft="8px">{selectedSource.label}</Typography>
                                    <div className="Border" />
                                    <ConfigEditIcon className="EditIcon" />
                                    <Link marginLeft="10px" href="#" underline="none">
                                        {t('wizard.data-source.edit-button')}
                                    </Link>
                                </Stack>
                            ) : null}
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails data-testid="step1DataSelectContent">
                        <Stack direction="column" spacing={2}>
                            <div>
                                <div>
                                    {' '}
                                    {t('wizard.data-source.description-1')}{' '}
                                    <b>{t('wizard.data-source.description-2')} </b>
                                </div>
                                <div> </div>
                            </div>
                            <CleanStack direction="row" columnGap={2}>
                                {mockSources.map((source) =>
                                    renderSource(source, () => {
                                        setnextButtonDisable(false);
                                        setSource(source);
                                    }),
                                )}
                            </CleanStack>
                        </Stack>
                        <Stack>
                            <Button
                                onClick={() => handleExpandedChange('import-data')}
                                className={nextButtonDisable ? 'RefreshBtnDisabled' : 'RefreshBtn'}
                                disabled={nextButtonDisable}
                                data-testid="data-source-select-next-btn"
                            >
                                {t('common.next')}{' '}
                                {<ArrowDownIcon className={nextButtonDisable ? 'ArrowIconDisabled' : 'ArrowIcon'} />}
                            </Button>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
                {selectedSource?.name === DataSourceType.svmx && (
                    <Accordion
                        disabled={nextButtonDisable}
                        expanded={expanded === 'import-data'}
                        onChange={handleStepChange('import-data')}
                    >
                        <AccordionSummary
                            aria-controls="content2"
                            expanded={expanded === 'import-data'}
                            id="id2"
                            title={t('wizard.data-source.transfer-data')}
                            thisStep={2}
                            totalSteps={3}
                            stepTitle={t('common.accordian-step-title')}
                            data-testid="step2TransferDataTitleContainer"
                            dataTestIdTitle="step2TransferDataTitle"
                            dataTestIdStep="step2TransferDataStep"
                        />
                        <AccordionDetails data-testid="step2TransferDataContent">
                            <SFConnector
                                progress={showProgress}
                                config_id=""
                                callback={SFConnectorCallback}
                                loggedIn={loggedIn}
                            />
                        </AccordionDetails>
                    </Accordion>
                )}
                {selectedSource?.name === DataSourceType.csv && (
                    <UploadCsv
                        progress={showProgress}
                        configStore={configStore}
                        creating
                        expanded={expanded}
                        onHandleCreateStepChange={handleExpandedChange}
                    />
                )}
                {selectedSource?.name === undefined && (
                    <Accordion disabled>
                        <AccordionSummary
                            aria-controls="content2"
                            expanded={false}
                            id="id2"
                            title={t('wizard.data-source.transfer-data')}
                            thisStep={2}
                            totalSteps={3}
                            stepTitle={t('common.accordian-step-title')}
                            data-testid="step2TransferDataTitleContainer"
                            dataTestIdTitle="step2TransferDataTitle"
                            dataTestIdStep="step2TransferDataStep"
                        />
                    </Accordion>
                )}
            </Stack>
        </CleanStack>
    );
});

export default DataSource;
